import { useState, useMemo, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import WTMonogram from '../components/WTMonogram';
import AuthLegalFooter from '../components/AuthLegalFooter';
import '../styles/login.css';
import { BACKEND_URL } from '../config';

function getPasswordStrength(pw: string): { level: number; label: string } {
  if (pw.length === 0) return { level: 0, label: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  if (score <= 1) return { level: 1, label: 'Faible' };
  if (score === 2) return { level: 2, label: 'Moyen' };
  if (score === 3) return { level: 3, label: 'Bon' };
  return { level: 4, label: 'Excellent' };
}

function isPasswordAcceptable(pw: string): boolean {
  return pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw);
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const pseudo = searchParams.get('pseudo') || '';
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const pwStrength = useMemo(() => getPasswordStrength(password), [password]);
  const pwAcceptable = useMemo(() => isPasswordAcceptable(password), [password]);
  const passwordsMatch = passwordConfirm.length === 0 || password === passwordConfirm;

  if (!pseudo || !token) {
    return (
      <div className="login-page">
        <div className="login-bg" />
        <div className="login-card">
          <div className="login-header">
            <WTMonogram />
            <h1 className="login-title">Webtarot</h1>
            <p className="login-tagline">Lien invalide</p>
          </div>
          <div className="oauth-callback-content">
            <div className="login-error">Le lien de réinitialisation est invalide ou a expiré.</div>
            <Link to="/forgot-password" className="oauth-back-link">Demander un nouveau lien</Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo, token, new_password: password }),
      });

      const data = await res.json();

      if (!res.ok) {
        const messages: Record<string, string> = {
          invalid_token: 'Le lien a expiré ou est invalide. Demandez un nouveau lien.',
          token_expired: 'Le lien a expiré. Demandez un nouveau lien.',
          password_too_short: 'Le mot de passe est trop court.',
        };
        throw new Error(messages[data.error] || data.message || 'Erreur');
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="login-page">
        <div className="login-bg" />
        <div className="login-card">
          <div className="login-header">
            <WTMonogram />
            <h1 className="login-title">Webtarot</h1>
            <p className="login-tagline">Mot de passe réinitialisé</p>
          </div>
          <div className="oauth-callback-content">
            <div className="success-message">
              Votre mot de passe a été mis à jour avec succès.
            </div>
            <Link to="/" className="login-button" style={{ textAlign: 'center', display: 'block' }}>
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-bg" />
      <div className="login-card">
        <div className="login-header">
          <WTMonogram />
          <h1 className="login-title">Webtarot</h1>
          <p className="login-tagline">Nouveau mot de passe</p>
        </div>

        <div className="login-body">
          <p className="forgot-intro">
            Choisissez un nouveau mot de passe pour le compte <strong>{pseudo}</strong>.
          </p>

          <form onSubmit={handleSubmit} className="login-form">
            {error && <div className="login-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="new-password">Nouveau mot de passe</label>
              <input
                type="password"
                id="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 car., majuscule, minuscule, chiffre"
                autoComplete="new-password"
                autoFocus
                required
              />
              {password.length > 0 && (
                <div className="password-strength">
                  <div className="password-strength-bar">
                    <div className="password-strength-fill" data-level={pwStrength.level} />
                  </div>
                  <div className="password-strength-text" data-level={pwStrength.level}>
                    {pwStrength.label}
                    {!pwAcceptable && <> — {password.length < 8 ? '8 caractères min.' : 'majuscule, minuscule et chiffre requis'}</>}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirm-password">Confirmer</label>
              <input
                type="password"
                id="confirm-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Retapez votre mot de passe"
                autoComplete="new-password"
                required
              />
              {!passwordsMatch && (
                <div className="pseudo-check taken">Les mots de passe ne correspondent pas</div>
              )}
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading || !pwAcceptable || password !== passwordConfirm}
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </form>

          <div className="login-links">
            <Link to="/">Retour à la connexion</Link>
          </div>

          <AuthLegalFooter />
        </div>
      </div>
    </div>
  );
}

